/**
 * The money model: handover rev7 §5.4 (the per-head summary and the delivery
 * line), §5.11 (*Arithmetic* and *Invoice lines*) and §12.
 *
 * The arithmetic of §5.11, exactly: a category net is the sum of its lines'
 * quantities × net unit prices, held internally and never shown. VAT is
 * calculated on the category net and rounded to whole forints; the category
 * gross is net plus VAT. The delivery fee parameters are gross and the net is
 * derived. Every total is the sum of the rows above it.
 */

import { add, divide, multiply, rational, roundHalfUpToInteger, type Rational } from './rational.ts'
import type { BuyerType, MoneyParams, OrderLine, PriceCategory } from './types.ts'

const HUNDRED = rational(100n)

/** VAT on a net amount, rounded to whole forints (§5.11). */
export const vatOn = (net: number, vatPct: Rational): number =>
  roundHalfUpToInteger(divide(multiply(rational(net), vatPct), HUNDRED))

/** The net behind a gross amount. The zone fees of §12 are given gross. */
export const netOfGross = (gross: number, vatPct: Rational): number =>
  roundHalfUpToInteger(divide(multiply(rational(gross), HUNDRED), add(HUNDRED, vatPct)))

export interface CategoryTotal {
  category: PriceCategory
  lines: OrderLine[]
  /** §5.11: the category's total quantity. */
  portions: number
  /** §5.11: *ebből gluténmentes: N db*, inside *Sós* and *Édes*. */
  glutenFreePortions: number
  /**
   * §5.11 and DR-11 (`build/FIXES.md` item 14): *db* for pieces, *adag* for
   * salads, vegetable sticks and fruit; a category row mixing the two shows
   * the number with no unit, so this is null.
   */
  unit: string | null
  net: number
  vat: number
  gross: number
}

/** §5.11: one category row, with its VAT taken on the category net. */
export function categoryTotal(
  category: PriceCategory,
  lines: readonly OrderLine[],
  vatPct: Rational,
): CategoryTotal {
  const net = lines.reduce((sum, line) => sum + line.lineNet, 0)
  const vat = vatOn(net, vatPct)
  const units = [...new Set(lines.map((line) => line.unit))]
  return {
    category,
    lines: [...lines],
    portions: lines.reduce((sum, line) => sum + line.orderedPortions, 0),
    /* Run 17 item B5(b): the gluten-free guests' food is counted wherever it
       sits — on a separate gluten-free line, or carried inside the quantity
       of a gluten-free dish that is already on the menu. */
    glutenFreePortions: lines.reduce(
      (sum, line) =>
        sum + (line.isGlutenFreePortion ? line.orderedPortions : line.glutenFreePortions ?? 0),
      0,
    ),
    unit: units.length === 1 ? (units[0] ?? null) : null,
    net,
    vat,
    gross: net + vat,
  }
}

export interface FoodTotal {
  categories: CategoryTotal[]
  net: number
  vat: number
  gross: number
}

/** §5.11: the closing row that gives food and drinks together. */
export function foodTotal(categories: readonly CategoryTotal[]): FoodTotal {
  return {
    categories: [...categories],
    net: categories.reduce((sum, category) => sum + category.net, 0),
    vat: categories.reduce((sum, category) => sum + category.vat, 0),
    gross: categories.reduce((sum, category) => sum + category.gross, 0),
  }
}

export interface DeliveryLine {
  zone: string
  /** True for *Személyes átvétel*, which is 0 Ft and has no zone fee. */
  pickup: boolean
  /** §12: the fee charged, gross. 0 where free, quoted, picked up or blocked. */
  feeGross: number
  feeNet: number
  feeVat: number
  /** Zone C: *egyedi*. The fee is quoted, not charged from the table. */
  quoted: boolean
  /** Above the zone's free-delivery threshold: *ingyenes*. */
  free: boolean
  /**
   * The page has no slot that ends by the start time, so there is no delivery
   * to charge for (`build/FIXES.md` item 32). The order is blocked either way.
   */
  blocked: boolean
  /** §11.4: this zone routes the order to review. */
  review: boolean
  /** §8.1: the minimum order for this zone, or `min_pickup` on pickup. */
  minimumGross: number
  /**
   * §5.4 `free_delivery_hint`: how much more food makes delivery free,
   * compared with the **gross** food value. Null where no hint applies.
   */
  remainingToFreeGross: number | null
}

/**
 * §5.4 and §5.11 point 3: the delivery line. The threshold is compared with
 * the gross food value whatever the buyer type; §5.4 shows the remaining
 * amount net to a business buyer, which is presentation, not arithmetic.
 */
export function deliveryLine(
  args: { zone: string; pickup: boolean; deliveryBlocked?: boolean; foodGross: number },
  params: MoneyParams,
): DeliveryLine {
  const entry = params.zones[args.zone]
  if (entry === undefined) throw new Error(`deliveryLine: no zone ${JSON.stringify(args.zone)}`)
  const blocked = !args.pickup && (args.deliveryBlocked ?? false)
  let feeGross = 0
  let quoted = false
  let free = false
  if (!args.pickup && !blocked) {
    if (entry.feeQuoted || entry.feeGross === null) quoted = true
    else if (entry.freeAboveGross !== null && args.foodGross >= entry.freeAboveGross) free = true
    else feeGross = entry.feeGross
  }
  const feeNet = netOfGross(feeGross, params.vatPct)
  const noHint = args.pickup || blocked || quoted || free || entry.freeAboveGross === null
  return {
    zone: args.zone,
    pickup: args.pickup,
    feeGross,
    feeNet,
    feeVat: feeGross - feeNet,
    quoted,
    free,
    blocked,
    review: !args.pickup && entry.review,
    minimumGross: args.pickup ? params.minPickupGross : entry.minOrderGross,
    remainingToFreeGross: noHint ? null : (entry.freeAboveGross ?? 0) - args.foodGross,
  }
}

/**
 * **Run 19 §16: a service line that is not the delivery fee.**
 *
 * Coffee is the first. It is priced out of the register rather than from a
 * catalogue row (`coffee.ts`), so it is not a `PriceCategory` and cannot
 * ride in `FoodTotal`; and it is a service the customer is charged for, so
 * it cannot sit outside the order total either. §5.11 point 3 already has a
 * *Szolgáltatás* table with `Felszerelés` and `Személyzet` in it and no
 * amounts; this is the shape an amount takes when one of them has one.
 *
 * The net is the figure the engine works in and the VAT is taken on it, the
 * same way a category's is.
 */
export interface ServiceLine {
  /** `coffee`, `coffee_machine`, and whatever §5.11 point 3 adds later. */
  key: string
  /** Cups, machines — whatever the unit price is per. */
  quantity: number
  /** The net price of one, to the whole forint. §5.11's arithmetic. */
  unitNet: number
  net: number
  vat: number
  gross: number
}

/** One service line, with its VAT taken on its own net, as a category's is. */
export function serviceLine(
  key: string,
  quantity: number,
  unitNet: number,
  vatPct: Rational,
): ServiceLine {
  const net = quantity * unitNet
  const vat = vatOn(net, vatPct)
  return { key, quantity, unitNet, net, vat, gross: net + vat }
}

export interface OrderTotal {
  food: FoodTotal
  delivery: DeliveryLine
  /**
   * Run 19 §16: the priced service lines that are not the delivery fee —
   * today the coffee and the machine. Empty on every order that has none,
   * which is every order placed before this run.
   */
  services: ServiceLine[]
  net: number
  vat: number
  gross: number
}

/** §5.11 point 4: *Összesen*. Every total is the sum of the rows above it. */
export function orderTotal(
  food: FoodTotal,
  delivery: DeliveryLine,
  services: readonly ServiceLine[] = [],
): OrderTotal {
  const serviceNet = services.reduce((sum, line) => sum + line.net, 0)
  const serviceVat = services.reduce((sum, line) => sum + line.vat, 0)
  const serviceGross = services.reduce((sum, line) => sum + line.gross, 0)
  return {
    food,
    delivery,
    services: [...services],
    net: food.net + delivery.feeNet + serviceNet,
    vat: food.vat + delivery.feeVat + serviceVat,
    gross: food.gross + delivery.feeGross + serviceGross,
  }
}

/**
 * **Run 19 §15 (DECIDED 20261018, Barna): what the minimum order is
 * measured on.**
 *
 *   > `min_pickup` and the zone minimums count every line of the order,
 *   > gross: food, drinks, coffee, staff and equipment. Counting food only
 *   > would leave customers working out which lines qualify.
 *
 * **The delivery fee is not counted** (PROPOSED), because counting it would
 * lower each zone's threshold by that zone's own fee — a customer in the
 * zone with the dearer delivery would reach the minimum on less food than
 * one next door.
 *
 * So: the order gross, less the delivery fee. One function, because the
 * line the page shows, the block that refuses the order and the server's
 * own check all have to measure the same thing.
 */
export const minimumBasisGross = (total: OrderTotal): number =>
  total.gross - total.delivery.feeGross

export interface SummaryAmount {
  net: number
  vat: number
  gross: number
  /** What §5.11 prints for this buyer: gross for a consumer, net for a business. */
  shown: number
}

export interface SummaryView {
  buyer: BuyerType
  categories: { category: PriceCategory; portions: number; amount: SummaryAmount }[]
  /** Run 19 §16: the priced service lines, in the basis this buyer reads. */
  services: { key: string; quantity: number; amount: SummaryAmount }[]
  delivery: SummaryAmount
  total: SummaryAmount
  /** §5.11: *per fő*, and *Vendégenként, nettó* for a business buyer. */
  perGuest: SummaryAmount
  /**
   * §5.4: the remaining-to-free amount as this buyer sees it — net for a
   * business buyer, gross for a consumer. The comparison itself is gross.
   */
  remainingToFreeShown: number | null
}

/**
 * §5.11 *Gross and net*: a consumer sees every amount gross and labelled
 * *bruttó*; a business buyer sees category and service amounts net, then
 * *Nettó összesen*, *ÁFA 27%*, *Bruttó összesen, fizetendő* and
 * *Vendégenként, nettó*. This closes B16.
 *
 * Per-guest figures divide by the whole guest count, gluten-free guests
 * included (§5.4).
 */
export function summaryView(
  total: OrderTotal,
  args: { buyer: BuyerType; guests: number },
  params: MoneyParams,
): SummaryView {
  const show = (amount: { net: number; vat: number; gross: number }): SummaryAmount => ({
    ...amount,
    shown: args.buyer === 'business' ? amount.net : amount.gross,
  })
  const guests = Math.max(args.guests, 1)
  const perGuest = {
    net: Math.round(total.net / guests),
    vat: Math.round(total.vat / guests),
    gross: Math.round(total.gross / guests),
  }
  const remaining = total.delivery.remainingToFreeGross
  return {
    buyer: args.buyer,
    categories: total.food.categories.map((category) => ({
      category: category.category,
      portions: category.portions,
      amount: show(category),
    })),
    services: total.services.map((line) => ({
      key: line.key,
      quantity: line.quantity,
      amount: show(line),
    })),
    delivery: show({
      net: total.delivery.feeNet,
      vat: total.delivery.feeVat,
      gross: total.delivery.feeGross,
    }),
    total: show(total),
    perGuest: show(perGuest),
    remainingToFreeShown:
      remaining === null
        ? null
        : args.buyer === 'business'
          ? netOfGross(remaining, params.vatPct)
          : remaining,
  }
}

/**
 * §5.11 *Invoice lines* (PROPOSED, accountant with B12): one line per
 * category, quantity in pieces, net unit price = category net ÷ quantity to
 * two decimals, then the service lines, with VAT per line — so the invoice
 * does not reintroduce the per-dish prices the page withholds.
 *
 * The unit price is a presentation of the category net and never the figure
 * the line total is built from; multiplying a two-decimal unit price by the
 * quantity would not add up to the page.
 */
export interface InvoiceLine {
  kind: 'category' | 'service'
  /** The price category, or the service key (`delivery`). */
  key: string
  quantity: number
  /** Net unit price to two decimals, as a string, so nothing re-rounds it. */
  netUnitPrice: string
  net: number
  vat: number
  gross: number
}

export function invoiceLines(total: OrderTotal): InvoiceLine[] {
  const lines: InvoiceLine[] = total.food.categories.map((category) => ({
    kind: 'category' as const,
    key: category.category,
    quantity: category.portions,
    netUnitPrice: (category.portions > 0 ? category.net / category.portions : category.net).toFixed(2),
    net: category.net,
    vat: category.vat,
    gross: category.gross,
  }))
  /* §5.11 point 3: *Felszerelés* and *Személyzet* exist in the price model and
     never carry an amount on the automatic lane, because staff and equipment
     requests go to the enquiry lane (§5.9).
     Run 19 §16: coffee is the first service that does carry one, as two
     lines — the machine and the cups. The one-line-per-kind rule of
     2026-10-09 is kept: each is one line with its own quantity and its own
     net unit price, and the per-dish prices the page withholds are not
     reintroduced by either. */
  for (const line of total.services) {
    lines.push({
      kind: 'service',
      key: line.key,
      quantity: line.quantity,
      netUnitPrice: line.unitNet.toFixed(2),
      net: line.net,
      vat: line.vat,
      gross: line.gross,
    })
  }
  if (total.delivery.feeGross > 0) {
    lines.push({
      kind: 'service',
      key: 'delivery',
      quantity: 1,
      netUnitPrice: total.delivery.feeNet.toFixed(2),
      net: total.delivery.feeNet,
      vat: total.delivery.feeVat,
      gross: total.delivery.feeGross,
    })
  }
  return lines
}
